import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { readPreference } from './preferences';

/* ============================================================
   Haptic feedback — thin wrapper.
   ------------------------------------------------------------
   In the OR, the surgeon wears gloves and looks at the C-arm,
   not the phone. A subtle tap-back when a capture lands confirms
   the action without requiring eyes-on-screen.

   The plugin is a no-op on the web (Cloudflare Pages PWA) — we
   short-circuit there to avoid the dev-console warnings from
   `@capacitor/haptics`'s web fallback.

   API kept intentionally tiny: one verb (`haptic`), four kinds.
   Add more only when a new clinical interaction needs them.
   ============================================================ */

const isNative = Capacitor.isNativePlatform();

export type HapticKind = 'tap' | 'success' | 'warning' | 'error';

export async function haptic(kind: HapticKind = 'tap'): Promise<void> {
  if (!isNative) return;
  // Respect the user's preference (Account → Preferences → Haptic feedback).
  // Reading synchronously avoids racing the toggle on the same tap.
  if (!readPreference('hapticsEnabled')) return;
  try {
    switch (kind) {
      case 'tap':
        await Haptics.impact({ style: ImpactStyle.Light });
        return;
      case 'success':
        await Haptics.notification({ type: NotificationType.Success });
        return;
      case 'warning':
        await Haptics.notification({ type: NotificationType.Warning });
        return;
      case 'error':
        await Haptics.notification({ type: NotificationType.Error });
        return;
    }
  } catch {
    // Hardware/simulator doesn't support haptics — ignore.
  }
}
